import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApidaeTouristicObject } from '@alterrego/module-apidae';
import { Card, Badge } from '@alterrego/core-ui';
import heroImg from '../assets/hero-hmv.jpg';
import './HomeScreen.css';

const T = {
  heroTitle: 'Haute Maurienne Vanoise',
  heroSub: 'Communauté de Communes • Savoie',
  meteoTitle: 'Météo',
  meteoCondition: 'Ensoleillé',
  meteoCity: 'Modane',
  meteoTempC: 14,
  agendaTitle: "Agenda",
  agendaBadge: "Aujourd'hui",
  agendaEmpty: 'Aucun événement planifié',
  agendaSeeAll: 'Voir tous les événements →',
  explorerTitle: 'Explorer le territoire',
  explorerSub: 'Lieux, activités, restaurants & hébergements',
  quotidienTitle: 'Au Quotidien',
  quotidienSub: 'Services publics, transports & urgences',
  quotidienTeaser: '6 services · 4 lignes · numéros d\'urgence',
  actualitesTitle: 'Actualités',
  actualitesSub: 'Dernières informations du territoire',
};

const BACKEND_URL = 'https://api.apps.alterrego.com';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function MeteoCard() {
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const dateCapitalized = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

  return (
    <Card variant="primary" className="cchmv-card cchmv-card-meteo">
      <div className="cchmv-card-row">
        <span className="cchmv-card-icon">☀️</span>
        <div className="cchmv-card-body">
          <p className="cchmv-card-title">{T.meteoTitle} · {T.meteoCity}</p>
          <p className="cchmv-card-sub">{dateCapitalized}</p>
          <div className="cchmv-meteo-row">
            <p className="cchmv-meteo-temp">{T.meteoTempC}°C</p>
            <p className="cchmv-meteo-detail">{T.meteoCondition}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function AgendaCard({ onTap }: { onTap: () => void }) {
  const [event, setEvent] = useState<ApidaeTouristicObject | null | 'loading'>('loading');
  const [numFound, setNumFound] = useState<number>(0);

  useEffect(() => {
    const today = todayISO();
    fetch(`${BACKEND_URL}/apidae/events?count=1&first=0&dateDebut=${today}&dateFin=${today}`)
      .then((r) => r.json())
      .then((d) => {
        setNumFound(d.numFound ?? 0);
        setEvent(d.items?.[0] ?? null);
      })
      .catch(() => setEvent(null));
  }, []);

  const countLabel = numFound > 0
    ? `${numFound} événement${numFound > 1 ? 's' : ''} aujourd'hui`
    : null;

  return (
    <Card className="cchmv-card" onClick={onTap} role="button">
      <div className="cchmv-card-row">
        <span className="cchmv-card-icon">📅</span>
        <div className="cchmv-card-body">
          <div className="cchmv-agenda-head">
            <Badge variant="primary" style={{ marginBottom: 6 }}>{T.agendaBadge}</Badge>
            {countLabel && <span className="cchmv-agenda-count-chip">{countLabel}</span>}
          </div>
          <p className="cchmv-card-title">{T.agendaTitle}</p>
          {event === 'loading' && <p className="cchmv-agenda-empty">Chargement…</p>}
          {event === null && <p className="cchmv-agenda-empty">{T.agendaEmpty}</p>}
          {event && event !== 'loading' && (
            <>
              <p className="cchmv-agenda-event-title">{event.nom?.libelleFr ?? 'Événement'}</p>
              <p className="cchmv-agenda-event-lieu">
                {event.localisation?.adresse?.commune?.nom ?? ''}
              </p>
            </>
          )}
          <p className="cchmv-card-sub" style={{ marginTop: 6 }}>{T.agendaSeeAll}</p>
        </div>
        <span className="cchmv-card-arrow">›</span>
      </div>
    </Card>
  );
}

function NavCard({
  icon, title, sub, teaser, onClick,
}: { icon: string; title: string; sub: string; teaser?: string; onClick: () => void }) {
  return (
    <Card className="cchmv-card" onClick={onClick} role="button">
      <div className="cchmv-card-row">
        <span className="cchmv-card-icon">{icon}</span>
        <div className="cchmv-card-body">
          <p className="cchmv-card-title">{title}</p>
          <p className="cchmv-card-sub">{sub}</p>
          {teaser && <p className="cchmv-card-teaser">{teaser}</p>}
        </div>
        <span className="cchmv-card-arrow">›</span>
      </div>
    </Card>
  );
}

export function HomeScreen() {
  const navigate = useNavigate();

  return (
    <div className="cchmv-home">
      <div className="cchmv-hero" style={{ backgroundImage: `url(${heroImg})` }}>
        <div className="cchmv-hero-overlay" />
        <div className="cchmv-hero-content">
          <p className="cchmv-hero-eyebrow">🏔 Bienvenue dans la</p>
          <h1 className="cchmv-hero-title">{T.heroTitle}</h1>
          <p className="cchmv-hero-sub">{T.heroSub}</p>
        </div>
      </div>

      <div className="cchmv-cards">
        <MeteoCard />

        <AgendaCard onTap={() => navigate('/agenda')} />

        <p className="cchmv-section-label">Découvrir & services</p>

        <NavCard
          icon="🧭"
          title={T.explorerTitle}
          sub={T.explorerSub}
          onClick={() => navigate('/explorer')}
        />

        <NavCard
          icon="🏛"
          title={T.quotidienTitle}
          sub={T.quotidienSub}
          teaser={T.quotidienTeaser}
          onClick={() => navigate('/au-quotidien')}
        />

        <NavCard
          icon="📰"
          title={T.actualitesTitle}
          sub={T.actualitesSub}
          onClick={() => navigate('/actualites')}
        />
      </div>
    </div>
  );
}
